import React, { useCallback, useEffect, useRef, useState } from "react";
import { MdPeople, MdSearch } from "react-icons/md";
import { toast } from "react-toastify";
import api from "../../../services/axios";
import { getProfiles } from "../../../services/profileApi";
import { getPessoas } from "../../../services/identidadeApi";
import { PessoaLista } from "../../../interfaces/Identidade";
import { Profile } from "../../../interfaces/Profile";
import PessoasTable from "./components/PessoasTable";
import PessoaPainel, { ClienteOpcao } from "./components/PessoaPainel";

const DEBOUNCE_MS = 300;

const BTN_SEC =
  "rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5";

/**
 * Administração de pessoas (permissão `identidade.admin`): quem é a pessoa, em quais organizações
 * ela tem vínculo e com quais perfis. A busca espera 300 ms de pausa na digitação.
 */
const PessoasPage: React.FC = () => {
  const [entrada, setEntrada] = useState("");
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [lista, setLista] = useState<PessoaLista | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [selecionada, setSelecionada] = useState<number | null>(null);
  const [perfisAtivos, setPerfisAtivos] = useState<Profile[]>([]);
  const [clientesAtivos, setClientesAtivos] = useState<ClienteOpcao[]>([]);
  const reqId = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setBusca(entrada);
      setPagina(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [entrada]);

  const carregar = useCallback(async () => {
    const id = ++reqId.current;
    try {
      const dados = await getPessoas(busca, pagina);
      if (id !== reqId.current) return; // resposta de uma busca já superada
      setLista(dados);
      setErro(false);
    } catch {
      if (id === reqId.current) setErro(true);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [busca, pagina]);

  useEffect(() => {
    setLoading(true);
    carregar();
  }, [carregar]);

  useEffect(() => {
    Promise.all([getProfiles(), api.get("/Client/Clients")])
      .then(([perfis, clientes]) => {
        setPerfisAtivos(perfis.filter((p) => p.flActive));
        setClientesAtivos(
          (clientes.data as any[])
            .filter((c) => c.flActive)
            .map((c) => ({ id: c.id, nome: c.tradeName || c.legalName }))
            .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))
        );
      })
      .catch(() => toast.error("Falha ao carregar perfis e clientes para os vínculos."));
  }, []);

  const totalPaginas = lista ? Math.max(1, Math.ceil(lista.total / lista.tamanhoPagina)) : 1;

  return (
    <div className="mt-5 animate-fade-up">
      <header className="mb-6 flex items-center gap-4">
        <span
          className="dv-aura flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow"
          aria-hidden="true"
        >
          <MdPeople className="h-6 w-6" />
        </span>
        <div className="min-w-0">
          <span className="inline-flex items-center rounded-full bg-dv-blue-500/10 px-3 py-1 font-display text-xs font-bold uppercase tracking-[0.18em] text-dv-blue-600 dark:text-dv-cyan-300">
            Identidade
          </span>
          <h1 className="mt-1.5 text-2xl font-extrabold tracking-tight text-navy-700 dark:text-white">
            Pessoas
          </h1>
        </div>
      </header>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <label htmlFor="busca-pessoas" className="sr-only">
            Buscar por nome ou e-mail
          </label>
          <MdSearch
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
          <input
            id="busca-pessoas"
            value={entrada}
            onChange={(e) => setEntrada(e.target.value)}
            placeholder="Buscar por nome ou e-mail"
            className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
          />
        </div>
        {lista && !erro && (
          <span className="text-sm tabular-nums text-gray-600 dark:text-gray-400">
            {lista.total} {lista.total === 1 ? "pessoa" : "pessoas"}
          </span>
        )}
      </div>

      {erro ? (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300"
        >
          <span>Não foi possível carregar as pessoas. Verifique a conexão e tente de novo.</span>
          <button
            type="button"
            onClick={() => {
              setLoading(true);
              carregar();
            }}
            className="rounded-xl border border-red-300 px-3 py-1.5 text-xs font-semibold hover:bg-red-100 dark:border-red-500/40 dark:hover:bg-red-900/30"
          >
            Tentar de novo
          </button>
        </div>
      ) : (
        <PessoasTable
          itens={lista?.itens ?? []}
          busca={busca}
          loading={loading}
          selectedId={selecionada}
          onSelect={setSelecionada}
        />
      )}

      {!erro && lista && totalPaginas > 1 && (
        <nav className="mt-4 flex items-center justify-end gap-3" aria-label="Paginação">
          <button
            type="button"
            disabled={pagina <= 1 || loading}
            onClick={() => setPagina((p) => p - 1)}
            className={BTN_SEC}
          >
            Anterior
          </button>
          <span className="text-sm tabular-nums text-gray-600 dark:text-gray-400">
            {pagina} de {totalPaginas}
          </span>
          <button
            type="button"
            disabled={pagina >= totalPaginas || loading}
            onClick={() => setPagina((p) => p + 1)}
            className={BTN_SEC}
          >
            Próxima
          </button>
        </nav>
      )}

      {selecionada !== null && (
        <PessoaPainel
          pessoaId={selecionada}
          perfisAtivos={perfisAtivos}
          clientesAtivos={clientesAtivos}
          onClose={() => setSelecionada(null)}
          onChanged={carregar}
        />
      )}
    </div>
  );
};

export default PessoasPage;
